import {
  ActionIcon,
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Divider,
  Group,
  Loader,
  Modal,
  Paper,
  ScrollArea,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { FormResponseViewer, downloadFormAsPdf } from '../../lib/FormResponseViewer';
import { downloadFhirAttachment } from '../../lib/downloadFhirAttachment';
import type { MedplumClient } from '@medplum/core';
import { useMedplum } from '@medplum/react-hooks';
import {
  IconDownload,
  IconEye,
  IconFileText,
  IconPencil,
  IconSignature,
} from '@tabler/icons-react';
import type { QuestionnaireResponse } from '@medplum/fhirtypes';
import { SearchInput } from '../../components/SearchInput';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClientPortalLayout } from './components/ClientPortalLayout';
import { useClientPortalDocuments } from './hooks/useClientPortalDocuments';
import type { ClientPortalDocumentRow } from './utils/clientPortalFhirTypes';

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US');
  } catch {
    return iso;
  }
}

function statusColor(status: string): string {
  if (status === 'Completed') return 'green';
  if (status === 'Pending') return 'gold';
  if (status === 'Shared') return 'blue';
  return 'gray';
}

/**
 * Download an attachment from a row without opening a new tab.
 */
async function downloadRowAttachment(
  medplum: MedplumClient,
  attachment: ClientPortalDocumentRow['attachment'],
  filename: string,
): Promise<void> {
  if (!attachment) {
    notifications.show({ color: 'yellow', title: 'Download unavailable', message: 'No attachment data available for this file.' });
    return;
  }
  try {
    await downloadFhirAttachment(attachment, filename, medplum);
  } catch (err) {
    notifications.show({
      color: 'red',
      title: 'Download failed',
      message: err instanceof Error ? err.message : 'Could not download this file.',
    });
  }
}

export function ClientPortalDocumentsPage(): JSX.Element {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const { documents, loading, error } = useClientPortalDocuments();
  const [query, setQuery] = useState('');

  // QR viewer state for completed forms
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerRow, setViewerRow] = useState<ClientPortalDocumentRow | null>(null);
  const [viewerQr, setViewerQr] = useState<QuestionnaireResponse | null>(null);
  const [viewerLoading, setViewerLoading] = useState(false);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return documents;
    return documents.filter((r) => r.name.toLowerCase().includes(q));
  }, [documents, query]);

  const openQrViewer = async (row: ClientPortalDocumentRow): Promise<void> => {
    if (!row.questionnaireResponseId) return;
    setViewerRow(row);
    setViewerQr(null);
    setViewerLoading(true);
    setViewerOpen(true);
    try {
      const qr = await medplum.readResource('QuestionnaireResponse', row.questionnaireResponseId);
      setViewerQr(qr);
    } catch {
      notifications.show({ color: 'red', title: 'Could not load form', message: 'The completed form response could not be loaded.' });
      setViewerOpen(false);
    } finally {
      setViewerLoading(false);
    }
  };

  const handleRowAction = (row: ClientPortalDocumentRow): void => {
    if (row.status === 'Pending' && row.taskId) {
      navigate(`/client-portal/assessments/${row.taskId}`, {
        state: { returnTo: '/client-portal/documents' },
      });
    } else if (row.status === 'Completed' && row.kind === 'Form' && row.questionnaireResponseId) {
      void openQrViewer(row);
    } else if (row.kind === 'File') {
      void downloadRowAttachment(medplum, row.attachment, row.name);
    }
  };

  return (
    <ClientPortalLayout
      title="Documents"
      description="Complete pending forms and download shared files."
      localhostOnly
    >
      <Paper p="lg" withBorder>
        <Group justify="space-between" mb="md">
          <Title order={3}>Your documents</Title>
          <SearchInput
            placeholder="Search documents..."
            value={query}
            onChange={(e) => setQuery(e.currentTarget.value)}
            w={320}
          />
        </Group>

        {loading ? (
          <Group justify="center" py="xl"><Loader size="sm" /></Group>
        ) : error ? (
          <Alert variant="light" color="red" title="Could not load documents">
            <Text size="sm">{error}</Text>
          </Alert>
        ) : filtered.length === 0 ? (
          <Text size="sm" c="gray.6">
            {query ? 'No documents match your search.' : 'No documents.'}
          </Text>
        ) : (
          <ScrollArea type="auto" offsetScrollbars>
            <Table highlightOnHover style={{ minWidth: 560 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Status</Table.Th>
                  <Table.Th>Date</Table.Th>
                  <Table.Th />
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filtered.map((row) => (
                  <Table.Tr
                    key={row.id}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleRowAction(row)}
                  >
                    <Table.Td>
                      <Group gap="sm" wrap="nowrap">
                        {row.needsSignature
                          ? <IconSignature size={16} stroke={1.5} style={{ color: 'var(--mantine-color-blue-6)', flexShrink: 0 }} />
                          : <IconFileText size={16} stroke={1.5} style={{ color: 'var(--mantine-color-gray-6)', flexShrink: 0 }} />
                        }
                        <Text size="sm" fw={500}>{row.name}</Text>
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={statusColor(row.status)} size="sm">
                        {row.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="gray.6">{row.date ? formatDate(row.date) : '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group justify="flex-end" gap="xs">
                        {row.status === 'Pending' && (
                          <Button
                            size="xs"
                            leftSection={row.needsSignature ? <IconSignature size={14} /> : <IconPencil size={14} />}
                            onClick={(e) => { e.stopPropagation(); handleRowAction(row); }}
                          >
                            {row.needsSignature ? 'Sign' : 'Start'}
                          </Button>
                        )}
                        {row.status === 'Completed' && row.kind === 'Form' && row.questionnaireResponseId && (
                          <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={(e) => { e.stopPropagation(); void openQrViewer(row); }}
                            aria-label="View completed form"
                          >
                            <IconEye size={16} />
                          </ActionIcon>
                        )}
                        {row.kind === 'File' && (
                          <ActionIcon
                            variant="light"
                            color="blue"
                            onClick={(e) => { e.stopPropagation(); void downloadRowAttachment(medplum, row.attachment, row.name); }}
                            aria-label="Download"
                          >
                            <IconDownload size={16} />
                          </ActionIcon>
                        )}
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
      </Paper>

      {/* Completed Form Viewer Modal */}
      <Modal
        opened={viewerOpen}
        onClose={() => { setViewerOpen(false); setViewerRow(null); setViewerQr(null); }}
        title={viewerRow?.name ?? 'Completed Form'}
        size="xl"
        centered
        styles={{
          body: { paddingTop: 0, paddingBottom: 'var(--mantine-spacing-lg)' },
          content: { maxHeight: '92vh', display: 'flex', flexDirection: 'column' },
          header: { marginBottom: 0, paddingBottom: 'var(--mantine-spacing-sm)' },
        }}
      >
        {viewerRow && (
          <Stack gap="md" style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
            <Group justify="flex-start" style={{ flexShrink: 0 }}>
              <Badge variant="light" size="sm" color="green">
                Completed {viewerRow.date ? formatDate(viewerRow.date) : ''}
              </Badge>
            </Group>

            <Divider style={{ flexShrink: 0 }} />

            <Card
              withBorder
              p="lg"
              bg="gray.0"
              style={{
                borderColor: 'var(--mantine-color-gray-3)',
                flex: 1,
                minHeight: 0,
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <ScrollArea.Autosize mah={560} offsetScrollbars type="scroll" style={{ flex: 1 }}>
                <Box pb="lg" pr="xs">
                  {viewerLoading ? (
                    <Group justify="center" py="xl"><Loader size="sm" /></Group>
                  ) : viewerQr ? (
                    <FormResponseViewer questionnaireResponse={viewerQr} />
                  ) : (
                    <Text c="gray.6" ta="center" py="xl">Could not load form response.</Text>
                  )}
                </Box>
              </ScrollArea.Autosize>
            </Card>

            <Divider style={{ flexShrink: 0 }} />

            <Group justify="space-between" style={{ flexShrink: 0 }}>
              <Button
                variant="default"
                onClick={() => { setViewerOpen(false); setViewerRow(null); setViewerQr(null); }}
              >
                Close
              </Button>
              {viewerQr && (
                <Button
                  variant="light"
                  leftSection={<IconDownload size={16} />}
                  onClick={() => void downloadFormAsPdf(viewerRow.name, viewerQr!)}
                >
                  Download PDF
                </Button>
              )}
            </Group>
          </Stack>
        )}
      </Modal>
    </ClientPortalLayout>
  );
}
